import React, { useState } from 'react';
import styled from 'styled-components';
import { TranslationBankModel } from 'models/TranslationBankModel';

interface IProps {
  model: TranslationBankModel;
  onClose: () => void;
  onSuccess: (bankId: number) => void;
}

const BankCreate: React.FC<IProps> = ({ model, onClose, onSuccess }) => {
  const [name, setName] = useState('');
  const [agentType, setAgentType] = useState('');
  const [isCreating, setIsCreating] = useState(false);
  const [error, setError] = useState('');

  const [agentTypeOptions, setAgentTypeOptions] = useState<
    Array<{ value: string; label: string; description: string }>
  >([]);
  const [loadingAgentTypes, setLoadingAgentTypes] = useState(true);

  // Fetch agent types on mount
  React.useEffect(() => {
    const fetchAgentTypes = async () => {
      const response = await model.getAgentTypes();
      if (response && response.agentTypes) {
        setAgentTypeOptions(response.agentTypes);
      }
      setLoadingAgentTypes(false);
    };
    fetchAgentTypes();
  }, [model]);

  const handleCreate = async () => {
    // Validation
    if (!name.trim()) {
      setError('Bank name is required');
      return;
    }
    if (!agentType) {
      setError('Agent type is required');
      return;
    }

    setError('');
    setIsCreating(true);

    try {
      // Set the item data on the model
      model.itemUpdated = {
        id: model.NEW_CREATE_ID,
        name: name.trim(),
        agentType,
        deletable: false,
      };

      // Create the bank
      await model.createItem();

      // Get the newly created bank ID
      const newBankId = model.itemId;
      if (newBankId) {
        onSuccess(newBankId);
      }
    } catch (err) {
      setError('Failed to create bank. Please try again.');
      setIsCreating(false);
    }
  };

  return (
    <Container>
      <Header>
        <Title>Create Translation Bank</Title>
        <CloseButton onClick={onClose}>×</CloseButton>
      </Header>

      <Content>
        <InfoBox>
          <InfoTitle>About Translation Banks</InfoTitle>
          <InfoList>
            <li>
              <strong>Purpose:</strong> Store validated translation examples to test AI agents and ensure quality
            </li>
            <li>
              <strong>How it works:</strong> Create banks for different agent types and add translation items for testing
            </li>
            <li>
              <strong>Testing:</strong> Use Agent QA jobs to validate agent performance against bank items
            </li>
          </InfoList>
        </InfoBox>

        <Section>
          <SectionTitle>Name *</SectionTitle>
          <Input
            type="text"
            value={name}
            onChange={(e) => setName(e.target.value)}
            placeholder="e.g., Spanish K-Lines Bank"
            disabled={isCreating}
            autoFocus
          />
        </Section>

        <Section>
          <SectionTitle>Agent Type *</SectionTitle>
          <Select
            value={agentType}
            onChange={(e) => setAgentType(e.target.value)}
            disabled={isCreating || loadingAgentTypes}
          >
            {loadingAgentTypes ? (
              <option value="">Loading agent types...</option>
            ) : agentTypeOptions.length === 0 ? (
              <option value="">No agent types available</option>
            ) : (
              <>
                <option value="">Select agent type...</option>
                {agentTypeOptions.map((option) => (
                  <option key={option.value} value={option.value}>
                    {option.label}
                  </option>
                ))}
              </>
            )}
          </Select>
          {agentType && agentTypeOptions.find((opt) => opt.value === agentType)?.description && (
            <HelpText>{agentTypeOptions.find((opt) => opt.value === agentType)?.description}</HelpText>
          )}
        </Section>

        {error && <ErrorText>{error}</ErrorText>}
      </Content>

      <Footer>
        <CancelButton onClick={onClose} disabled={isCreating}>
          Cancel
        </CancelButton>
        <CreateButton onClick={handleCreate} disabled={isCreating || loadingAgentTypes}>
          {isCreating ? 'Creating...' : 'Create Bank'}
        </CreateButton>
      </Footer>
    </Container>
  );
};

export default BankCreate;

const Container = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
  background-color: white;
`;

const Header = styled.div`
  display: flex;
  justify-content: space-between;
  align-items: center;
  padding: 20px;
  border-bottom: 1px solid #e0e0e0;
`;

const Title = styled.h2`
  margin: 0;
  font-size: 24px;
`;

const CloseButton = styled.button`
  background: none;
  border: none;
  font-size: 30px;
  cursor: pointer;
  color: #666;

  &:hover {
    color: #333;
  }
`;

const Content = styled.div`
  flex: 1;
  padding: 20px;
  overflow-y: auto;
`;

const InfoBox = styled.div`
  border-left: 4px solid #2196f3;
  background: #e3f2fd;
  color: #1565c0;
  padding: 12px 14px;
  border-radius: 4px;
  margin-bottom: 20px;
`;

const InfoTitle = styled.div`
  font-weight: 600;
  margin-bottom: 8px;
  font-size: 15px;
`;

const InfoList = styled.ul`
  margin: 0;
  padding-left: 20px;
  li {
    line-height: 1.5;
    margin-bottom: 6px;
    font-size: 14px;
  }
`;

const Section = styled.div`
  margin-bottom: 20px;
`;

const SectionTitle = styled.h3`
  margin-bottom: 10px;
  font-size: 16px;
  color: #333;
`;

const Input = styled.input`
  width: 94%;
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;

  &:focus {
    outline: none;
    border-color: #2196f3;
    box-shadow: 0 0 0 2px rgba(33, 150, 243, 0.1);
  }

  &:disabled {
    background: #f5f5f5;
    cursor: not-allowed;
  }

  &::placeholder {
    color: #999;
  }
`;

const Select = styled.select`
  width: 97%;
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  background-color: white;

  &:focus {
    outline: none;
    border-color: #2196f3;
    box-shadow: 0 0 0 2px rgba(33, 150, 243, 0.1);
  }

  &:disabled {
    background: #f5f5f5;
    cursor: not-allowed;
  }
`;

const TextArea = styled.textarea`
  width: 94%;
  padding: 10px 12px;
  border: 1px solid #ddd;
  border-radius: 4px;
  font-size: 14px;
  resize: vertical;
  font-family: inherit;

  &:focus {
    outline: none;
    border-color: #2196f3;
    box-shadow: 0 0 0 2px rgba(33, 150, 243, 0.1);
  }

  &:disabled {
    background: #f5f5f5;
    cursor: not-allowed;
  }

  &::placeholder {
    color: #999;
  }
`;

const HelpText = styled.div`
  font-size: 12px;
  color: #666;
  margin-top: 8px;
  font-style: italic;
`;

const ErrorText = styled.div`
  color: #d32f2f;
  font-size: 14px;
  margin-top: 10px;
  padding: 10px;
  background-color: #ffebee;
  border-radius: 4px;
`;

const Footer = styled.div`
  display: flex;
  justify-content: flex-end;
  gap: 10px;
  padding: 20px;
  border-top: 1px solid #e0e0e0;
`;

const CancelButton = styled.button`
  padding: 10px 20px;
  background-color: #f0f0f0;
  border: 1px solid #ddd;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;

  &:hover {
    background-color: #e0e0e0;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;

const CreateButton = styled.button`
  padding: 10px 20px;
  background-color: #2196f3;
  color: white;
  border: none;
  border-radius: 4px;
  cursor: pointer;
  font-size: 14px;

  &:hover:not(:disabled) {
    background-color: #1976d2;
  }

  &:disabled {
    opacity: 0.6;
    cursor: not-allowed;
  }
`;
